// Copyright (c) Docugami, Inc. All rights reserved.

import { Checkbox, CommandBarButton, ContextualMenu, DirectionalHint, IButtonProps, Icon, IconButton, Modal, Point, Stack, TooltipDelay, TooltipHost } from "@fluentui/react";
import { CardHeader } from "components/cards/CardHeader";
import { GrabHandleContainer } from "components/cards/CardStyles";
import { IInputField } from "components/cards/HintInputField";
import { LabelInputField } from "components/cards/LabelInputField";
import { LabelSearchDropdown } from "components/cards/LabelSearchDropdown";
import { dgFontFamily } from "components/common/Fonts";
import { addEntryWithInstancesThunk } from "components/reports/reportsActions";
import { appContentColor, disabledDarkGreyText, primaryTheme } from "components/shell/presentation/AppStyles";
import { RequestState, useAsyncHelper } from "hooks/AsyncHelper";
import { DgmlInstancesSingleton } from "hooks/documentDgml";
import { getChunkLabel } from "hooks/LabelChunk";
import { useLabelSearchSuggestions } from "hooks/LabelSearchSuggestions";
import { useLabelText } from "hooks/LabelText";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useReportEntryIdForChunk } from "hooks/reportEntryIdForLabel";
import { useSdvEntityQuery } from "hooks/SdvEntityQuery";
import { useSdvResource } from "hooks/SdvResource";
import { useWaitCursor } from "hooks/WaitCursor";
import { MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import { BoundsComponent } from "sdv/components/BoundsComponent";
import { TokenPositionComponent } from "sdv/components/TokenPositionComponent";
import { ViewComponent } from "sdv/components/ViewComponent";
import { ComponentId } from "sdv/engine/Component";
import { Engine } from "sdv/engine/Engine";
import { getPageForPos } from "sdv/Page";
import { worldToViewSpace } from "sdv/Space";
import { AppDispatch, AppState } from "store";
import { selectCanAddLabelToReport, selectReportId } from "store/reports/selectors";
import { selectCurrentChunkInstance } from "store/sdvSelectors";
import styled from "styled-components";

const InputContainer = styled.div`
    display: flex;
    flex: 1;
    padding: 0px 5px;
    box-sizing: border-box;
    align-items: center;
    justify-content: left;
    height: 32px;
`;

const DragRoot = styled.div`
    display: flex;
    width: 250px;
    cursor: move;
    flex-direction: column;
    position: relative;
`;

const Divider = styled.div`
    border-right: 1px solid grey;
    height: 32px;
`;

const GrabHandleRoot = styled(GrabHandleContainer)`
    padding: 0px 5px;
    margin-left: initial;
    :hover {
        cursor: move;
    }
`;

const HeaderRoot = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    border-bottom: 1px solid grey;
`;

interface IEditorButtonProps extends IButtonProps {
    tooltipContent: string | JSX.Element | JSX.Element[] | undefined;
}

interface ChunkEditorProps {
    engine: Engine | null;
    documentId: string;
    clusterId: string;
    viewRef: MutableRefObject<HTMLElement | undefined>;
    isEditing?: boolean;
    editLabel?: () => void;
    instancesCount?: number;
    instances?: string[];
}

const selectedChunkComponents: ComponentId[] = [ComponentId.Selected, ComponentId.Bounds];
const highlightChunkComponents: ComponentId[] = [ComponentId.Selected, ComponentId.Highlighter, ComponentId.TokenPosition];
const viewComponents: ComponentId[] = [ComponentId.View];

const defaultModalPosition = { left: 500, top: 300 };
export const ChunkEditor = (props: ChunkEditorProps): JSX.Element | null => {
    const [position, setPosition] = useState<Point>(defaultModalPosition);
    const [applyToAll, setApplyToAll] = useState(false);

    const instance = useAppSelector(selectCurrentChunkInstance);
    const isValidInstance = !!instance && !instance.isAddedChunk;
    const reportId = useAppSelector(selectReportId);

    const dispatch = useAppDispatch();
    const chunkLabel = useMemo(() => !!instance ? dispatch(getChunkEditorLabel(props.documentId, instance.spanId!)) : '', [instance, props.documentId, dispatch])
    const targetInstances = useMemo(() => applyToAll && !!props.instances ? props.instances : !!instance ? [instance.spanId!] : [], [applyToAll, props.instances, instance]);
    const labelText = useLabelText(props.clusterId, props.documentId, chunkLabel, undefined, targetInstances, false);

    const isWithinEntry = !!useReportEntryIdForChunk(instance);

    const { entities: selectedEntities } = useSdvEntityQuery(selectedChunkComponents, props.engine);
    const { entities: highlightEntities } = useSdvEntityQuery(highlightChunkComponents, props.engine);
    const { entities: viewEntities } = useSdvEntityQuery(viewComponents, props.engine);
    const pageTokens = useSdvResource("pageTokens", props.engine).value;
    const visiblePages = useSdvResource("visiblePages", props.engine).value;

    const { execute, state: addEntryState } = useAsyncHelper("Add entry to report");
    const canAddToReport = useAppSelector(state => selectCanAddLabelToReport(state, labelText.label, props.clusterId));

    const inputRef = useRef<IInputField>();
    const { suggestions, showSuggestions, setShowSuggestions } = useLabelSearchSuggestions(labelText.label, props.clusterId, undefined, !labelText.isEditing);

    useWaitCursor(addEntryState === RequestState.InProgress);

    useEffect(() => {
        if (!isValidInstance) {
            return;
        }

        let selectedEntityBox = selectedEntities?.[0]?.getComponent<BoundsComponent>(ComponentId.Bounds)!.boundingBox;
        const highlightPositionToken = highlightEntities?.[0]?.getComponent<TokenPositionComponent>(ComponentId.TokenPosition)!.token;
        if (highlightPositionToken !== undefined && pageTokens) {
            selectedEntityBox = pageTokens[highlightPositionToken];
        }

        const view = viewEntities?.[0]?.getComponent<ViewComponent>(ComponentId.View)!;
        if (!!selectedEntityBox && !!view && !!props.viewRef.current) {
            const leftTopPoint = { x: selectedEntityBox.left, y: selectedEntityBox.top };
            const viewPosition = view.viewport.worldPosition;
            const canvasBounds = props.viewRef.current.getBoundingClientRect();
            const positionViewSpace = worldToViewSpace(leftTopPoint, viewPosition);

            let position = {
                left: positionViewSpace.x + canvasBounds.x,
                top: positionViewSpace.y + canvasBounds.y - 70
            };

            // If the target position is not on any visible page, use the default modal position
            if (!getPageForPos(visiblePages, position.top)) {
                position = defaultModalPosition;
            }

            setPosition(position);
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isValidInstance, selectedEntities, highlightEntities, viewEntities, props.viewRef])

    useEffect(() => {
        setApplyToAll(false);
    }, [instance])

    // Early out and return null if the current instance is within an active report entry
    if (isWithinEntry) {
        return null;
    }

    const revertLabelChangesActionProps: IEditorButtonProps = {
        onClick: labelText.undo,
        styles: { root: { opacity: labelText.isEditing ? 1 : 0, cursor: labelText.isEditing ? 'pointer' : 'inherit' } },
        iconProps: {
            iconName: 'Undo',
            styles: { root: { fontSize: '14px' } }
        },
        tooltipContent: "Revert"
    }

    const addToReportDisabled = !labelText.label || !labelText.isValid || !reportId || !canAddToReport;
    const getAddToReportTooltip = () => {
        if (!canAddToReport && !!labelText.label) {
            return "This label is already in the report, or the report items cannot be modified."
        }
        else if (!labelText.label) {
            return "Label this instance to add it to your report"
        }
        else if (!labelText.isValid) {
            return "This label is not valid"
        }
    }

    const acceptLabelActionProps: IEditorButtonProps = {
        disabled: !labelText.isValid,
        onClick: labelText.saveLabel,
        iconProps: {
            iconName: 'Accept',
            styles: { root: { fontSize: '16px', color: primaryTheme.palette.themePrimary } }
        },
        tooltipContent: "Save label"
    }

    const getButton = (props: IEditorButtonProps) =>
        <TooltipHost content={props.tooltipContent} delay={TooltipDelay.zero}><IconButton {...props} /></TooltipHost>;

    async function addToReport() {
        if (!reportId || !targetInstances.length || !labelText.isValid || !canAddToReport) {
            return;
        }

        if (labelText.isEditing) {
            labelText.saveLabel();
        }

        execute(dispatch(addEntryWithInstancesThunk(reportId, {
            folderId: props.documentId,
            label: labelText.label,
            spanIds: targetInstances
        })));
    }

    const showFooter = !!labelText.label;
    const footer = (
        <CardHeader count={props.instancesCount ?? 0} showSingle>
            <TooltipHost content={getAddToReportTooltip()} delay={TooltipDelay.zero} directionalHint={DirectionalHint.bottomCenter}>
                <CommandBarButton
                    title='Add to report'
                    text="ADD TO REPORT"
                    styles={{
                        root: { fontFamily: dgFontFamily, color: !addToReportDisabled ? appContentColor : disabledDarkGreyText, fontSize: "10px" },
                        label: { fontWeight: 500 },
                        textContainer: { flex: 'none' }
                    }}
                    iconProps={{
                        iconName: "CircleAddition",
                        styles: { root: { fontSize: '11px', color: addToReportDisabled ? primaryTheme.palette.neutralTertiary : appContentColor } },
                    }}
                    disabled={addToReportDisabled}
                    onClick={addToReport} />
            </TooltipHost>
        </CardHeader>
    )

    const showApplyToAllCheckbox = labelText.isEditing && !!props.instancesCount && props.instancesCount > 1;
    const applyToAllCheckbox = (
        <HeaderRoot>
            <Checkbox
                styles={{
                    root: { fontSize: "5px" },
                    text: { fontSize: "10px", color: appContentColor },
                    checkbox: { height: "12px", width: "12px", placeSelf: "center" },
                    checkmark: { fontSize: "10px" }
                }}
                onChange={(_, checked) => setApplyToAll(!!checked)}
                checked={applyToAll}
                label={`Apply to all ${props.instancesCount!} instances?`} />
            <TooltipHost
                content={`Apply changes to all instances for this label?`}
                directionalHint={DirectionalHint.rightCenter}
                styles={{ root: { display: "flex", height: "fit-content", width: "fit-content", placeSelf: "center" } }}
                delay={TooltipDelay.zero}>
                <Icon iconName="Info" styles={{ root: { fontSize: "10px", cursor: "default", padding: "0px 5px", boxSizing: "border-box" } }} />
            </TooltipHost>
        </HeaderRoot>
    )

    return (
        <Modal
            isOpen={isValidInstance && !props.isEditing}
            isBlocking={false}
            isDarkOverlay={false}
            isModeless={true}
            isClickableOutsideFocusTrap={true}
            forceFocusInsideTrap={false}
            scrollableContentClassName="noscroll"
            styles={{
                root: { width: 'unset', height: 'fit-content', left: position.left, top: position.top },
                main: { minHeight: 'unset', minWidth: 'unset', maxWidth: 'unset', borderRadius: '3px', border: `2px solid ${labelText.isEditing ? primaryTheme.palette.themePrimary : primaryTheme.palette.neutralLighter}` }
            }}
            dragOptions={{
                moveMenuItemText: 'Move',
                closeMenuItemText: 'Close',
                menu: ContextualMenu,
                dragHandleSelector: ".dragTarget"
            }}>
            <DragRoot className='dragTarget'>
                <Stack horizontal styles={{ root: { width: "100%", borderBottom: showApplyToAllCheckbox || showFooter ? "1px solid grey" : "" } }}>
                    <GrabHandleRoot>
                        <Icon iconName="CalculatorEqualTo" />
                    </GrabHandleRoot>
                    <InputContainer>
                        <LabelSearchDropdown
                            suggestions={suggestions}
                            showSuggestions={showSuggestions}
                            onDismiss={() => setShowSuggestions(false)}
                            onSelectSuggestion={suggestion => {
                                labelText.setLabel(suggestion);
                                setShowSuggestions(false);
                                inputRef.current?.focus();
                            }}>
                            <LabelInputField {...labelText} inputRef={inputRef} small />
                        </LabelSearchDropdown>
                    </InputContainer>
                    {labelText.isEditing ? <><Divider />{getButton(revertLabelChangesActionProps)}</> : null}
                    {labelText.isEditing ? <><Divider />{getButton(acceptLabelActionProps)}</> : null}
                </Stack>
                {showApplyToAllCheckbox && applyToAllCheckbox}
                {showFooter && footer}
            </DragRoot>
        </Modal>
    )
}

// Get the label for the current token range
const getChunkEditorLabel = (folderId: string, spanId: string) => (dispatch: AppDispatch, getState: () => AppState): string => {
    const instance = DgmlInstancesSingleton.getInstanceById(folderId, spanId);
    if (!!instance) {
        return dispatch(getChunkLabel(instance));
    }

    return '';
}